import { creatorbaseQueries, getAllInfluencerPostGroups, InfluencerPostGroup, TiktokInfluencerPost } from '@round/api';
import { QueryClient } from '@tanstack/react-query';
import { InstagramInfluencerPost } from '../types/Instagram.types';

export type PostMoveCostFields = {
    base_cost: string;
    currency_id: number;
};

/**
 * The cost fields to send when moving a post to another campaign. Moving strips the post's payment
 * details server-side, so the request has to carry a base cost for them to be rebuilt from.
 */
export async function getPostMoveCostFields(
    queryClient: QueryClient,
    post: TiktokInfluencerPost | InstagramInfluencerPost,
    sourceGroup: InfluencerPostGroup,
    destinationGroup: InfluencerPostGroup,
    platform: 'tiktok' | 'instagram'
): Promise<PostMoveCostFields | undefined> {
    // Request fresh data so the share state is as accurate as possible.
    const freshGroups = await getAllInfluencerPostGroups(platform, destinationGroup.influencer_plan).catch(() => [
        // Fall back to the data held in state.
        sourceGroup,
        destinationGroup,
    ]);

    const getIsShared = (group: InfluencerPostGroup) =>
        freshGroups.find(({ id }) => id === group.id)?.is_shared_with_creatorbase ?? group.is_shared_with_creatorbase;

    // Destination is not shared, so converted post cost is not relevant.
    if (!getIsShared(destinationGroup)) {
        return undefined;
    }

    // Source group is not shared, so no PostPaymentDetails will exist for the post and the cost is
    // not converted yet. We can provide the current cost as base to generate a converted cost.
    if (!getIsShared(sourceGroup)) {
        if (!post.cost) {
            return undefined;
        }

        return { base_cost: post.cost, currency_id: post.currency_id };
    }

    if (!post.influencer_post_id) {
        return undefined;
    }

    // The source and destination groups are shared. When the post's group is changed, the BE strips
    // and recreates related payment objects, so we will fetch the current PostPaymentDetails in
    // order to sustain the base cost. staleTime 0 because a cached copy may describe records an
    // earlier move already replaced.
    try {
        const paymentDetails = await queryClient.fetchQuery({
            ...creatorbaseQueries.postPaymentDetails.options.listAll({ post_id: post.influencer_post_id }),
            staleTime: 0,
        });
        const unrequested = paymentDetails.find((detail) => detail.payment_request_status === 'UNREQUESTED');

        if (!unrequested) {
            return undefined;
        }

        return { base_cost: String(unrequested.base_cost), currency_id: unrequested.currency_id };
    } catch {
        return undefined;
    }
}
